<template>
  <div class="journey-table-container">
    <table class="content-table">
      <thead class="header-table">
      <tr class="trhead">
        <th @click="handleSort('name')" class="sortable-header">
          {{$t('program')}}
          <img
              src="../../../assets/images/status.svg"
              class="sort-icon"
              :class="getSortClass('name')"
          >
        </th>
        <th>{{$t('description')}}</th>
        <th @click="handleSort('userAssignedCount')" class="sortable-header">
          {{$t('assigned_users')}}
          <img
              src="../../../assets/images/status.svg"
              class="sort-icon"
              :class="getSortClass('userAssignedCount')"
          >
        </th>
        <th @click="handleSort('startAt')" class="sortable-header">
          {{$t('date')}}
          <img
              src="../../../assets/images/status.svg"
              class="sort-icon"
              :class="getSortClass('startAt')"
          >
        </th>
      </tr>
      </thead>
      <tbody class="tbody-container">
      <tr class="tr-class" v-if="!loading" v-for="program in programs" :key="program.programId">
        <td class="td-style">
          <router-link class="dropdown-option" :to="'/journey-programs/' + program.programId">
            <div class="name-row">
              <img class="company-icon" :src="program.avatarUrl" v-if="program.avatarUrl" />
              <span class="test-class" :title="program.name">{{ truncateText(program.name, 80) }}</span>
            </div>
          </router-link>
        </td>

        <td class="td-style">
          <span class="active-tag" :title="program.description">{{ truncateText(program.description, 80) }}</span>
        </td>
        <td class="td-style">
          <span class="tooltip-container">{{program.userAssignedCount}} (
           <span class="tooltip-text">{{$t('total_users')}}</span>
          </span>
          <span class="detail-blue tooltip-container">
            {{program.totalUserStarted}}
            <span class="tooltip-text">{{$t('users_started')}}</span>
          </span> |
          <span class="detail-red tooltip-container">
            {{program.totalUserNotStarted}}
            <span class="tooltip-text">{{$t('users_not_started')}}</span>
          </span>)
        </td>

        <td class="td-style">
          <template v-if="program.startAt && program.finishAt">
            <span :class="['test-class', { 'date-expired': isDateExpired(program.finishAt) }]">
              {{ formatDateRange(program.startAt, program.finishAt) }}
            </span>
          </template>
          <template v-else-if="program.startAt">
            <span class="test-class">{{ formatDate(program.startAt) }} - N/A</span>
          </template>
          <template v-else>
            <em class="no-data-text">N/A</em>
          </template>
        </td>

      </tr>

      <tr v-else>
        <td colspan="4" class="table-loading">
          <VueSpinner size="16" height="100%" color="#8c8c8c" />
          <span class="text">{{$t('loading')}}</span>
        </td>
      </tr>

      <tr v-if="!loading && programs.length === 0 ">
        <td colspan="4" class="table-loading">
          <span class="text">{{$t('no_data_found')}}</span>
        </td>
      </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import AddUserModal from '@/components/modals/InviteUserModal.vue';
import FilterModal from '@/components/tables/Company/CompanyFilterModal.vue';
import CompanyFieldFilter from '@/components/table-filters/CompanyFieldFilter.vue';
import ProgramFilter from "@/components/table-filters/NumberFilter.vue";
import UserAmountFilter from "@/components/table-filters/MinMaxNumberFilter.vue";
import PaymentVolume from "@/components/table-filters/PaymentVolume.vue";
import DateFilter from "@/components/table-filters/DateFilter.vue";
import ExpandableSearch from "@/components/ExpandableSearch.vue";
import {VueSpinner} from "vue3-spinners";
import ActionDropdown from "@/components/ActionDropdown.vue";
import {handleErrorResponse, truncate} from "@/utils/utils";
import JourneyUserFilters from "@/components/tables/Journey/JourneyUserFilters.vue";

export default {
  components: {
    JourneyUserFilters,
    ActionDropdown,
    DateFilter,
    PaymentVolume,
    UserAmountFilter,
    AddUserModal,
    FilterModal,
    CompanyFieldFilter,
    ProgramFilter,
    ExpandableSearch,
    VueSpinner
  },
  props: {
    loading: {
      type: Boolean,
      default: true
    },
    programs: {
      type: Array,
      required: true
    },
    refreshData: {
      type: Function,
      default: () => {}
    },
    currentSort: {
      type: Object,
      default: () => ({ column: null, direction: null })
    },
    onSort: {
      type: Function,
      required: false,
      default: () => {}
    }
  },
  data() {
    return {
      showFilter:false,
      searchText: '',
    }
  },

  methods: {
    handleSort(column) {
      let newDirection = 'asc';

      if (this.currentSort.column === column) {
        if (this.currentSort.direction === 'asc') {
          newDirection = 'desc';
        } else if (this.currentSort.direction === 'desc') {
          this.onSort(null, null);
          return;
        }
      }

      this.onSort(column, newDirection);
    },

    getSortClass(column) {
      if (this.currentSort.column !== column) {
        return 'sort-default';
      }

      if (this.currentSort.direction === 'asc') {
        return 'sort-asc';
      } else if (this.currentSort.direction === 'desc') {
        return 'sort-desc';
      }

      return 'sort-default';
    },

    formatDate(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);

      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();

      return `${day}.${month}.${year}`;
    },

    formatDateRange(startDate, endDate) {
      const start = this.formatDate(startDate);
      const end = this.formatDate(endDate);
      return `${start} - ${end}`;
    },

    isDateExpired(dateString) {
      if (!dateString) return false;

      const finishDate = new Date(dateString);
      const today = new Date();

      today.setHours(0, 0, 0, 0);
      finishDate.setHours(0, 0, 0, 0);

      return finishDate < today;
    },

    truncate,
    handleSearch(event) {
      this.searchText = event.target.value;
    },

    truncateText(text, maxLength) {
      if (!text) return "";
      return text.length > maxLength ? text.substring(0, maxLength) + "..." : text;
    },
  },

}
</script>

<style>
.journey-table-container {
  width: 100%;
  max-width: 100%;
}

.sortable-header {
  cursor: pointer;
  user-select: none;
  transition: background-color 0.2s;
}

.sortable-header:hover {
  background-color: #f5f5f5;
}

.sort-icon {
  width: 16px;
  height: 16px;
  margin-left: 4px;
  vertical-align: middle;
  transition: transform 0.3s ease, opacity 0.3s ease;
  display: inline-block;
  opacity: 0.4;
}

.sortable-header:hover .sort-icon {
  opacity: 0.6;
}

.sort-icon.sort-asc,
.sort-icon.sort-desc {
  opacity: 1;
}

.sort-icon.sort-asc {
  transform: rotate(0deg);
}

.sort-icon.sort-desc {
  transform: rotate(180deg);
}

.sort-icon.sort-default {
  transform: rotate(0deg);
  opacity: 0.4;
}

.company-icon {
  height: 32px;
  width: 32px;
  border-radius: 50%;
}

.name-row {
  display: flex;
  flex-direction: row;
  gap: 10px;
  cursor: pointer;
}

.name-row span {
  font-family: euclid_medium, sans-serif;
  color: #000;
}

.name-row:hover.name-row span{
  color: #4A68F8;
}

.dropdown-body {
  position: absolute;
  background-color: #f1f1f1;
  padding: 10px;
  border-radius: 8px;
  top: 40px;
  min-width: 120px;
  display: flex;
  flex-direction: column;
  gap: 5px;
  z-index: 999999;
}

.dropdown-option {
  cursor: pointer;
  font-size: 12px;
  padding: 7px 15px;
  display: block;
  border-radius: 8px;
}

.dropdown-option:hover {
  background: #ffffff;
}

.action-wrapper {
  position: relative;
}

.bottom-actions {
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
  margin-bottom: 20px;
}

.selected-label {
  font-family: euclid_medium, sans-serif;
  font-size: 10px;
  color: #000;
  font-feature-settings: 'clig' off, 'liga' off;
  font-style: normal;
  line-height: 16px;
}

.active-tag {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #4A68F8;
  font-feature-settings: 'clig' off, 'liga' off;
  font-style: normal;
  line-height: 16px;
}

.tooltip-container {
  position: relative;
  display: inline-block;
}

.detail-blue {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #4A68F8;
  font-feature-settings: 'clig' off, 'liga' off;
  font-style: normal;
  line-height: 16px;
  cursor: pointer;
}

.detail-red {
  font-family: euclid_medium, sans-serif;
  font-size: 12px;
  color: #ff2121;
  font-feature-settings: 'clig' off, 'liga' off;
  font-style: normal;
  line-height: 16px;
  cursor: pointer;
}

.tooltip-text {
  visibility: hidden;
  width: max-content;
  background-color: #333;
  color: #fff;
  text-align: center;
  border-radius: 6px;
  padding: 5px 10px;
  position: absolute;
  z-index: 1000;
  bottom: 125%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  transition: opacity 0.3s;
  font-size: 11px;
  font-family: euclid_medium, sans-serif;
  white-space: nowrap;
}

.tooltip-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  margin-left: -5px;
  border-width: 5px;
  border-style: solid;
  border-color: #333 transparent transparent transparent;
}

.tooltip-container:hover .tooltip-text {
  visibility: visible;
  opacity: 1;
}

.ended-tag {
  font-family: euclid_medium, sans-serif;
  font-size: 10px;
  color: #FF0000;
  font-feature-settings: 'clig' off, 'liga' off;
  font-style: normal;
  line-height: 16px;
}

.date-expired {
  color: #FF0000 !important;
}

.tr-class {
  position: relative;
}
</style>
